<template>
  <!--
    Menu escondido de herramientas de depuracion. Se abre con 5 taps en la version
    del sidebar (SocialMedia.vue) y se monta global en App.vue.

    Antes cada herramienta tenia su propio gesto: 7 taps en un hotspot invisible de la
    esquina para el log tracker, 5 taps en la version para el modo QA. El hotspot era
    ademas un div fijo con z-index 99999 que se comia los taps de esa esquina para
    TODOS los usuarios. Un solo gesto, un solo menu.
  -->
  <q-dialog v-model="open" position="bottom">
    <q-card class="DiagnosticsDialog">
      <q-card-section class="row items-center q-py-sm bg-grey-9 text-white">
        <div class="text-subtitle2">
          {{ $t('diagnostics.title') }}
        </div>
        <q-space />
        <q-btn icon="close" dense flat v-close-popup />
      </q-card-section>

      <div class="DiagnosticsDialog-env">
        {{ envLine }}
      </div>

      <!--
        Estado de la capa offline, SOLO LECTURA. El dato que importa es dbOpen: si la base
        no abrio, la app se comporta como si offline estuviera apagado y el usuario no puede
        cerrar auditorias. Antes esto solo se veia llamando window.__offlineV2Debug.status()
        desde una consola JS, o sea con un APK depurable que soporte no puede compilar.
      -->
      <div class="DiagnosticsDialog-offline">
        <div class="DiagnosticsDialog-offlineTitle">
          {{ $t('diagnostics.offlineTitle') }}
        </div>

        <div
          v-if="hasOfflineStatus"
          :class="dbStateClasses"
          class="DiagnosticsDialog-offlineState"
        >
          <q-icon :name="dbStateIcon" size="18px" />
          <div class="DiagnosticsDialog-offlineStateText">
            <div>{{ dbStateLabel }}</div>
            <div v-if="showDbFailHint" class="DiagnosticsDialog-offlineStateHint">
              {{ $t('diagnostics.offlineDbFailHint') }}
            </div>
          </div>
        </div>

        <div v-else class="DiagnosticsDialog-offlineState is-unknown">
          <q-icon name="help_outline" size="18px" />
          <div class="DiagnosticsDialog-offlineStateText">
            {{ $t('diagnostics.offlineUnavailable') }}
          </div>
        </div>

        <div
          v-for="row in offlineRows"
          :key="row.key"
          class="DiagnosticsDialog-offlineRow"
        >
          <span class="DiagnosticsDialog-offlineLabel">{{ row.label }}</span>
          <span class="DiagnosticsDialog-offlineValue">{{ row.value }}</span>
        </div>
      </div>

      <q-list separator>
        <!-- Solo en builds nativos: en web hay DevTools de verdad y el LogTracker ni se renderiza. -->
        <q-item v-if="isNative">
          <q-item-section>
            <q-item-label>{{ $t('diagnostics.logTracker') }}</q-item-label>
            <q-item-label caption>
              {{ $t('diagnostics.logTrackerHint') }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-toggle :model-value="logStore.enabled" @update:model-value="handleLogTrackerToggle" color="grey-9" />
          </q-item-section>
        </q-item>

        <q-item>
          <q-item-section>
            <q-item-label>{{ $t('diagnostics.qaMode') }}</q-item-label>
            <q-item-label caption>
              {{ $t('diagnostics.qaModeHint') }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-toggle :model-value="qaMode" @update:model-value="handleQaModeToggle" color="grey-9" />
          </q-item-section>
        </q-item>

        <q-item @click="handleCopyEnv" clickable>
          <q-item-section avatar>
            <q-icon name="content_copy" />
          </q-item-section>
          <q-item-section>
            {{ $t('diagnostics.copyEnv') }}
          </q-item-section>
        </q-item>
      </q-list>
    </q-card>
  </q-dialog>
</template>

<script>
// Quasar
import { Capacitor } from '@capacitor/core'

// Boot
import { offlineStatus } from '@/boot/offline'

// Utils
import { envSummary, envHeader } from '@/utils/envInfo'
import { logStore } from '@/utils/logTracker'

// Composables
import { useDiagnosticsMenu } from '@/composables/diagnostics'
import { useQaMode } from '@/offline/composables/qaMode'

// Campos del snapshot que se listan como detalle, en este orden. dbOpen NO esta aqui:
// va aparte y destacado arriba porque es el que da el diagnostico de un vistazo.
const OFFLINE_FIELDS = [
  { key: 'platform', label: 'diagnostics.offlinePlatform' },
  { key: 'backend', label: 'diagnostics.offlineBackend' },
  { key: 'sqlitePlugin', label: 'diagnostics.offlineSqlitePlugin' },
  { key: 'filesystem', label: 'diagnostics.offlineFilesystem' },
  { key: 'debugEnabled', label: 'diagnostics.offlineDebugEnabled' },
  { key: 'offlineFlag', label: 'diagnostics.offlineFlag' },
  { key: 'logEntries', label: 'diagnostics.offlineLogEntries' }
]

// Texto plano del snapshot para el portapapeles. Se deja en ingles y con los nombres
// tecnicos crudos: lo lee quien depura, no el usuario, y asi es buscable en el codigo.
function offlineStatusToText (status) {
  if (!status) return 'offline: status unavailable'
  const lines = [`offline.dbOpen: ${status.dbOpen}`]
  OFFLINE_FIELDS.forEach(field => lines.push(`offline.${field.key}: ${status[field.key]}`))
  return lines.join('\n')
}

export default {
  name: 'DiagnosticsDialog',
  data: () => ({
    // null = todavia no se leyo, o offlineStatus() reviento. Se rellena al abrir el dialogo.
    offlineSnapshot: null
  }),
  setup () {
    // Apertura del menu: ref a nivel de modulo, compartido con el gesto del sidebar.
    const { open } = useDiagnosticsMenu()

    // Modo QA offline: el composable ya persiste en localStorage y despierta al banner de sync.
    const { qaMode, toggle: toggleQaMode } = useQaMode()

    return { open, qaMode, toggleQaMode, logStore }
  },
  computed: {
    isNative () {
      return Capacitor.isNativePlatform()
    },
    envLine () {
      return envSummary()
    },
    hasOfflineStatus () {
      return !!this.offlineSnapshot
    },
    isDbOpen () {
      return !!(this.offlineSnapshot && this.offlineSnapshot.dbOpen)
    },
    showDbFailHint () {
      return this.hasOfflineStatus && !this.isDbOpen
    },
    dbStateClasses () {
      return { 'is-ok': this.isDbOpen, 'is-problem': !this.isDbOpen }
    },
    dbStateIcon () {
      return this.isDbOpen ? 'check_circle' : 'error'
    },
    dbStateLabel () {
      return this.isDbOpen ? this.$t('diagnostics.offlineDbOk') : this.$t('diagnostics.offlineDbFail')
    },
    offlineRows () {
      if (!this.offlineSnapshot) return []
      return OFFLINE_FIELDS.map(field => ({
        key: field.key,
        label: this.$t(field.label),
        value: this.formatOfflineValue(this.offlineSnapshot[field.key])
      }))
    }
  },
  methods: {
    // El feedback es la burbuja del bicho apareciendo, no hace falta notificar.
    handleLogTrackerToggle (value) {
      this.logStore.enabled = value
    },
    // Aqui si se notifica: el mensaje trae la instruccion de como compartir el reporte,
    // que es la mitad del punto de encender el modo.
    handleQaModeToggle () {
      const on = this.toggleQaMode()
      this.$q.notify({
        type: 'info',
        message: on ? this.$t('sync.qaModeOn') : this.$t('sync.qaModeOff')
      })
    },
    // Se relee cada vez que se abre el dialogo: un snapshot de hace media hora miente
    // justo en el caso que interesa (la base se cerro despues del ultimo render).
    readOfflineStatus () {
      try {
        this.offlineSnapshot = offlineStatus()
      } catch (error) {
        console.error(error)
        this.offlineSnapshot = null
      }
    },
    formatOfflineValue (value) {
      if (typeof value === 'boolean') return value ? this.$t('diagnostics.yes') : this.$t('diagnostics.no')
      if (value === null || value === undefined || value === '') return this.$t('diagnostics.empty')
      return String(value)
    },
    // Un solo boton: soporte pide "dale copiar y pegamelo" y recibe entorno + estado offline.
    handleCopyEnv () {
      this.readOfflineStatus()
      const text = `${envHeader()}\n${offlineStatusToText(this.offlineSnapshot)}`
      const done = () => this.$q.notify({ type: 'positive', message: this.$t('diagnostics.envCopied'), timeout: 1000 })
      const fail = () => this.$q.notify({ type: 'negative', message: this.$t('diagnostics.copyFailed'), timeout: 1000 })
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(fail)
      } else {
        fail()
      }
    },
    handleOpenChange (value) {
      if (value) this.readOfflineStatus()
    }
  },
  watch: {
    open: 'handleOpenChange'
  }
}
</script>

<style lang="scss" scoped>
.DiagnosticsDialog {
  width: 100%;

  &-env {
    padding: 6px 16px;
    background: #eceff1;
    border-bottom: 1px solid #cfd8dc;
    font-family: monospace;
    font-size: 10px;
    color: #37474f;
    word-break: break-all;
  }

  &-offline {
    padding: 10px 16px;
    border-bottom: 1px solid #cfd8dc;
  }

  &-offlineTitle {
    margin-bottom: 6px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #78909c;
  }

  &-offlineState {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;

    &.is-ok {
      background: #e8f5e9;
      color: #2e7d32;
    }

    &.is-problem {
      background: #ffebee;
      color: #c62828;
    }

    &.is-unknown {
      background: #eceff1;
      color: #546e7a;
    }
  }

  &-offlineStateHint {
    margin-top: 2px;
    font-size: 11px;
    font-weight: 400;
  }

  &-offlineRow {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding-top: 4px;
    font-size: 11px;
  }

  &-offlineLabel {
    color: #78909c;
  }

  &-offlineValue {
    font-family: monospace;
    color: #37474f;
    text-align: right;
    word-break: break-all;
  }
}
</style>
